import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Circle,
  Flex,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight, User, Users } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

// Import TasteMatch emojis for intro slides
import noCompatibilityEmoji from '@/assets/images/emojis/00_noCompatibilityEmoji.svg';
import lowCompatibilityEmoji from '@/assets/images/emojis/01_lowCompatibilityEmoji.svg';
import someCompatibilityEmoji from '@/assets/images/emojis/02_someCompatibilityEmoji.svg';
import mediumCompatibilityEmoji from '@/assets/images/emojis/03_mediumCompatibilityEmoji.svg';
import highCompatibilityEmoji from '@/assets/images/emojis/04_highCompatibilityEmoji.svg';
import mostCompatibilityEmoji from '@/assets/images/emojis/05_mostCompatibilityEmoji.svg';

import logoImage from '@/assets/images/logo.png';

const MotionBox = motion.create(Box);

interface IntroSlide {
  id: string;
  header: string;
  tagline?: string | React.ReactNode;
  body: string | React.ReactNode;
  visual: React.ReactNode;
}

const SWIPE_THRESHOLD = 50;
const SWIPE_VELOCITY = 500;

const introSlides: IntroSlide[] = [
  {
    id: 'welcome',
    header: 'WELCOME TO',
    tagline: 'FOOD + RESTAURANT GUIDE',
    body: (
      <>
        Because &#34;The Best&#34; is something<br />
        different for everyone.<br />
        <br />
        Paire displays the compatibility<br />
        between your own unique<br />
        PersonalTaste™ &amp; restaurants<br />
        everywhere.<br />
        <br />
        TasteMatching™ you with<br />
        places that are just your type.
      </>
    ),
    visual: <Image src={logoImage} alt="Paire Logo" height="150px" />,
  },
  {
    id: 'personal-taste',
    header: 'PersonalTaste™',
    body: (
      <>
        Build your PersonalTaste™ by<br />
        taking fun food quizzes &amp;<br />
        privately rating restaurants.<br />
        <br />
        The reliability of TasteMatches™<br />
        increases w/ every quiz you take &amp;<br />
        every restaurant you privately rate.
      </>
    ),
    visual: <User size={100} color="var(--color-secondary-green)" strokeWidth={1.5} />,
  },
  {
    id: 'taste-matching',
    header: 'TasteMatching™',
    tagline: (<>Nobody knows what you like<br />better than <em>you</em> do.</>),
    body: (
      <>
        That&apos;s why Paire tags restaurants<br />
        with colorful emojis that reflect<br />
        how closely each one matches<br />
        your own PersonalTaste™<br />
        <br />
        Every TasteMatch™ is customized<br />
        to be uniquely yours —<br />
        not generic or one-size-fits-all.
      </>
    ),
    visual: (
      <HStack spacing={3} justify="center" flexWrap="wrap">
        <Circle size="40px" bg="#f43636">
          <Image src={noCompatibilityEmoji} alt="None" boxSize="30px" />
        </Circle>
        <Circle size="40px" bg="#FFA03E">
          <Image src={lowCompatibilityEmoji} alt="Low" boxSize="30px" />
        </Circle>
        <Circle size="40px" bg="#fffd57">
          <Image src={someCompatibilityEmoji} alt="Some" boxSize="30px" />
        </Circle>
        <Circle size="40px" bg="#c2ff74">
          <Image src={mediumCompatibilityEmoji} alt="Medium" boxSize="30px" />
        </Circle>
        <Circle size="40px" bg="#53fa5b">
          <Image src={highCompatibilityEmoji} alt="High" boxSize="30px" />
        </Circle>
        <Circle size="40px" bg="#02cf0e">
          <Image src={mostCompatibilityEmoji} alt="Highest" boxSize="30px" />
        </Circle>
      </HStack>
    ),
  },
  {
    id: 'friends',
    header: 'TasteMatching™',
    tagline: 'Because great food is meant to be shared.',
    body: (
      <>
        Paire makes dining out with friends<br />
        easier by displaying overall group<br />
        compatibility with each restaurant<br />
        on your collaborative group lists.<br />
        <br />
        Connect with friends and see<br />
        your TasteMatches™,<br />
        try new places you&apos;ll love &amp;<br />
        collaborate to build group lists!
      </>
    ),
    visual: <Users size={100} color="var(--color-secondary-green)" strokeWidth={1.5} />,
  },
];

interface IntroCarouselProps {
  onLogin: () => void;
  onRegister: () => void;
}

export const IntroCarousel: React.FC<IntroCarouselProps> = ({ onLogin, onRegister }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // Track and navigate to signup
  const handleSignupClick = useCallback(() => {
    analytics.capture(AnalyticsEvents.SIGNUP_CLICKED, { source: 'intro_carousel' });
    onRegister();
  }, [onRegister]);

  // Track and navigate to login
  const handleLoginClick = useCallback(() => {
    analytics.capture(AnalyticsEvents.LOGIN_CLICKED, { source: 'intro_carousel' });
    onLogin();
  }, [onLogin]);

  const isFirstSlide = currentIndex === 0;
  const isLastSlide = currentIndex === introSlides.length - 1;
  const currentSlide = introSlides[currentIndex];

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  const paginate = (newDirection: number) => {
    const newIndex = currentIndex + newDirection;
    if (newIndex >= 0 && newIndex < introSlides.length) {
      setDirection(newDirection);
      setCurrentIndex(newIndex);
    }
  };

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    const { offset, velocity } = info;

    if (
      offset.x < -SWIPE_THRESHOLD ||
      velocity.x < -SWIPE_VELOCITY
    ) {
      // Swiped left - go next
      if (currentIndex < introSlides.length - 1) {
        paginate(1);
      }
    } else if (
      offset.x > SWIPE_THRESHOLD ||
      velocity.x > SWIPE_VELOCITY
    ) {
      // Swiped right - go previous
      if (currentIndex > 0) {
        paginate(-1);
      }
    }
  };

  const handleNext = () => {
    if (!isLastSlide) {
      paginate(1);
    }
  };

  return (
    <Flex
      direction="column"
      align="center"
      justify="space-between"
      minHeight="100vh"
      px={6}
      pb={10}
      // Safe-area inset ensures content clears the Dynamic Island on iPhone 14 Pro
      // and the rounded status bar region on Android
      pt="calc(48px + env(safe-area-inset-top))"
      position="relative"
      overflow="hidden"
      bg="var(--color-overlay-80)"
    >

      {/* Main content area with swipe */}
      <Box
        flex="1"
        width="100%"
        maxWidth={{ base: "400px", md: "500px" }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
      >
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <MotionBox
            key={currentSlide.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            width="100%"
            cursor="grab"
            _active={{ cursor: 'grabbing' }}
          >
            <VStack spacing={6} textAlign="center" px={2}>
              {/* Welcome slide: Logo + Tagline grouped tightly */}
              {isFirstSlide ? (
                <VStack spacing={0}>
                  <Box>{currentSlide.visual}</Box>
                  {currentSlide.tagline && (
                    <Text
                      fontSize="sm"
                      color="var(--color-secondary-green)"
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {currentSlide.tagline}
                    </Text>
                  )}
                </VStack>
              ) : (
                <Box mb={4}>{currentSlide.visual}</Box>
              )}

              {/* Non-welcome slides: Header */}
              {!isFirstSlide && (
                <Heading size="lg" color="white">
                  {currentSlide.header}
                </Heading>
              )}

              {/* Non-welcome slides: Tagline */}
              {!isFirstSlide && currentSlide.tagline && (
                <Text
                  fontSize="xl"
                  fontWeight="semibold"
                  color="var(--color-secondary-green)"
                >
                  {currentSlide.tagline}
                </Text>
              )}

              {/* Body */}
              <Text
                fontSize="md"
                color="gray.300"
                lineHeight="2"
                maxWidth={{ base: "350px", md: "450px" }}
              >
                {currentSlide.body}
              </Text>
            </VStack>
          </MotionBox>
        </AnimatePresence>
      </Box>

      {/* Bottom section: dots and button */}
      <VStack spacing={6} width="100%" maxWidth={{ base: "400px", md: "500px" }}>
        {/* Progress dots */}
        <HStack spacing={2}>
          {introSlides.map((slide, index) => (
            <Box
              key={slide.id}
              width={index === currentIndex ? '24px' : '8px'}
              height="8px"
              borderRadius="full"
              bg={index === currentIndex ? 'purple.500' : 'gray.600'}
              transition="all 0.3s ease"
              cursor="pointer"
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }}
            />
          ))}
        </HStack>

        {/* Navigation buttons */}
        <HStack width="100%" spacing={3} justify="center">
          {/* Back button - only show when not on first slide */}
          {currentIndex > 0 && (
            <Button
              size="lg"
              flex="1"
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
              _active={{ bg: 'transparent' }}
              onClick={() => paginate(-1)}
              leftIcon={<ChevronLeft size={20} />}
              css={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Back
            </Button>
          )}

          {/* Next/Sign Up button */}
          {isLastSlide ? (
            <Button
              size="lg"
              flex="1"
              bg="purple.500"
              color="white"
              _hover={{ bg: 'purple.600' }}
              _active={{ bg: 'purple.700' }}
              onClick={handleSignupClick}
              rightIcon={<ChevronRight size={20} />}
              css={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Sign Up
            </Button>
          ) : (
            <Button
              size="lg"
              flex="1"
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
              _active={{ bg: 'transparent' }}
              onClick={handleNext}
              rightIcon={<ChevronRight size={20} />}
              css={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Next
            </Button>
          )}
        </HStack>

        {/* Login button - always visible */}
        <Button
          variant="ghost"
          color="gray.400"
          fontWeight="normal"
          onClick={handleLoginClick}
          _hover={{ color: 'white' }}
          _active={{ bg: 'transparent' }}
          css={{ WebkitTapHighlightColor: 'transparent' }}
        >
          Already have an account? Login
        </Button>
      </VStack>
    </Flex>
  );
};

export default IntroCarousel;
